import React from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, change } from 'redux-form'
import { bindActionCreators } from 'redux'
import { SubmissionError } from 'redux-form'
import LoadingProgress from '../components/LoadingProgress'
import { fetchRemoteActions, addRemoteAction } from '../actions/remoteactions'
import { renderInputField, renderSelectField } from '../utils/forms'

const ACTION_META = {
  SINGLE_DOOR_OPEN: {
    label: 'Abrir locker',
    iconClass: 'fas fa-door-open',
    description: 'Se abrirá remotamente la puerta del locker. El cliente podrá acceder a sus pertenencias.',
    confirmText: 'Confirmar apertura',
    accentColor: '#10b981',
    bgGradient: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
    warning: null,
  },
  SINGLE_UNRENT: {
    label: 'Desalquilar locker',
    iconClass: 'fas fa-bluetooth',
    description: 'El locker quedará libre y disponible para nuevos alquileres.',
    confirmText: 'Confirmar desalquiler',
    accentColor: '#ef4444',
    bgGradient: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
    warning: 'Esta acción liberará el locker. El cliente perderá el acceso inmediatamente.',
  },
  SINGLE_BLOCKLATER: {
    label: 'Marcar QR / PDA',
    iconClass: 'fas fa-qrcode',
    description: 'El locker quedará reservado para acceso mediante código QR o terminal PDA.',
    confirmText: 'Confirmar reserva',
    accentColor: '#8b5cf6',
    bgGradient: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)',
    warning: null,
  },
  SINGLE_RESET_PIN: {
    label: 'Cambiar PIN',
    iconClass: 'fas fa-lock',
    description: 'Se restablecerá el PIN del locker.',
    confirmText: 'Confirmar cambio de PIN',
    accentColor: '#f59e0b',
    bgGradient: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
    warning: null,
  },
};

class RemoteActionCustomerSingleAddForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      actionCompleted: false,
      errorMessage: '',
      pendingAction: null,
    }

    this.submit = this.submit.bind(this)
    this.selectAction = this.selectAction.bind(this)
    this.cancelAction = this.cancelAction.bind(this)
  }

  async submit(props) {

    if (this.props.machine.Status !== 'OPERATIVE' && this.props.machine.Status !== 'LOCKERS_FULL' && this.props.machine.Status !== 'SLEEP') {
      alert("Acción bloqueada! La máquina no está online.");
      return 0;
    }

    this.setState({ loading: true, actionCompleted: false, errorMessage: '' });

    // Insert machine id to the query
    props.machine = this.props.machine._id
    props.executor = this.props.fullname

    try {
      const response = await this.props.addRemoteAction(props);

      if (response.error) {
        throw new SubmissionError({
          _error: response.error.message,
          ...response.error.fields
        });
      }

      // Wait for fetchRemoteActions to complete
      await this.waitForRemoteActionCompletion(response.response._id);

      this.setState({ loading: false, actionCompleted: true });

      // Inside submit(), before reloading:
      //window.removeEventListener('beforeunload', this.props.onBeforeUnload);

      // Reload the page after successful completion
      window.location.reload();

      return response;

    } catch (error) {
      this.setState({ loading: false, errorMessage: error.message || 'Error processing action' });
      throw error;
    }
  }

  waitForRemoteActionCompletion(actionId) {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {

        if (this.state.cancelled) {
          reject(new Error('Action cancelled by user'));
          return;
        }

        try {
          const response = await this.props.fetchRemoteActions(this.props.machine.Serial);
          const action = response.response.find(action => action._id === actionId);

          if (action && (action.status === 'COMPLETE' || action.status === 'ERROR')) {
            resolve(action);
          } else {
            setTimeout(checkStatus, 1000); // Check status again after 1 second
          }
        } catch (error) {
          reject(error);
        }
      };

      checkStatus();
    });
  }

  handleCancel() {
    this.setState({ loading: false, cancelled: true });
  }

  selectAction(code) {
    this.props.dispatch(change('RemoteActionCustomerSingleAddForm', 'code', code));
    this.props.dispatch(change('RemoteActionCustomerSingleAddForm', 'arguments', `${this.props.lockerMatricula}`));
    this.setState({ pendingAction: code });
  }

  cancelAction() {
    this.props.dispatch(change('RemoteActionCustomerSingleAddForm', 'code', ''));
    this.props.dispatch(change('RemoteActionCustomerSingleAddForm', 'arguments', ''));
    this.setState({ pendingAction: null });
  }

  render() {
    const { error, handleSubmit, machine, category, type, lockerStatus } = this.props
    const { loading, actionCompleted, pendingAction } = this.state
    const { lockerMatricula } = this.props

    if (loading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', gap: 12 }}>
          <span style={{ color: '#64748b', fontWeight: 600, fontSize: 14 }}>Ejecutando acción...</span>
          <LoadingProgress logoHeight={36} className="compact" />
        </div>
      );
    }

    if (actionCompleted) {
      return (
        <div className="flex justify-content-center">
          <div className="alert alert-success">Proceso completado</div>
          <div className="alert alert-success">Actualiza la página para ver los cambios en la matriz</div>
        </div>
      );
    }

    const meta = pendingAction ? ACTION_META[pendingAction] : null;

    return (
      <div>
        {/* Hidden fields – always rendered so redux-form tracks their values */}
        <div style={{ display: 'none' }}>
          <Field name="code" component={renderSelectField}>
            <option value="">-</option>
            <option value="SINGLE_DOOR_OPEN">Abrir</option>
            <option value="SINGLE_UNRENT">Desalquilar</option>
            <option value="SINGLE_BLOCKLATER">Reserva QR/PDA</option>
            <option value="SINGLE_RESET_PIN">Cambiar Pin</option>
          </Field>
          <Field name="arguments" type="text" component={renderInputField} placeholder="Argumentos" />
        </div>

        {pendingAction ? (
          /* ── Confirmation panel ── */
          <div style={{ borderRadius: 10, overflow: 'hidden', border: `2px solid ${meta.accentColor}`, boxShadow: `0 4px 20px ${meta.accentColor}33` }}>
            {/* Header */}
            <div style={{ background: meta.bgGradient, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className={meta.iconClass} style={{ fontSize: 20, color: '#fff' }}></i>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{meta.label}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
                  Locker <strong style={{ color: '#fff' }}>{machine ? Number(lockerMatricula) + machine.FirstIndex - 1 : lockerMatricula}</strong> &mdash; {machine && machine.Name}
                </div>
              </div>
            </div>
            {/* Body */}
            <div style={{ background: '#fff', padding: '20px' }}>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, margin: '0 0 14px' }}>
                {meta.description}
              </p>
              {meta.warning && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#d97706', marginTop: 2, flexShrink: 0 }}></i>
                  <span style={{ color: '#92400e', fontSize: 13, lineHeight: 1.5 }}>{meta.warning}</span>
                </div>
              )}
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={handleSubmit(this.submit)}
                  style={{
                    flex: 1, padding: '11px 0', borderRadius: 8, border: 'none',
                    background: meta.accentColor, color: '#fff',
                    fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  <i className={`${meta.iconClass} mr-2`}></i>{meta.confirmText}
                </button>
                <button
                  type="button"
                  onClick={this.cancelAction}
                  style={{
                    padding: '11px 22px', borderRadius: 8, border: '1px solid #d1d5db',
                    background: '#f9fafb', color: '#374151',
                    fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ── Action buttons grid ── */
          <div className="action-grid-2">
            {[
              {
                code: 'SINGLE_DOOR_OPEN',
                label: 'Abrir',
                iconClass: 'fas fa-door-open',
                color: '#10b981',
                bg: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
                disabled: category === 'MONTARTO',
              },
              {
                code: 'SINGLE_UNRENT',
                label: 'Desalquilar',
                iconClass: 'fas fa-sign-out-alt',
                color: '#ef4444',
                bg: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
                disabled: category === 'BAQUEIRA' || category === 'MONTARTO' || lockerStatus === 'FL',
              },
              {
                code: 'SINGLE_BLOCKLATER',
                label: 'Marcar QR / PDA',
                iconClass: 'fas fa-qrcode',
                color: '#8b5cf6',
                bg: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)',
                disabled: lockerStatus === 'FL' || ((category === 'BAQUEIRA' || category === 'MONTARTO' || type === 'LICENCIA_BASICA') && machine.Contract.Zone !== 6),
                pro: true,
              },
              {
                code: 'SINGLE_RESET_PIN',
                label: 'Cambiar PIN',
                iconClass: 'fas fa-lock',
                color: '#f59e0b',
                bg: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
                disabled: true,
              },
            ].map(({ code, label, iconClass, bg, disabled, pro }) => (
              <button
                key={code}
                type="button"
                onClick={() => this.selectAction(code)}
                disabled={disabled}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '18px 12px',
                  borderRadius: 10,
                  border: 'none',
                  background: disabled ? '#e5e7eb' : bg,
                  color: disabled ? '#9ca3af' : '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  transition: 'opacity 0.15s',
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                {pro && (
                  <span style={{
                    position: 'absolute',
                    top: 6,
                    right: 8,
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: 1,
                    padding: '2px 5px',
                    borderRadius: 4,
                    lineHeight: 1.4,
                  }}>PRO</span>
                )}
                <i className={iconClass} style={{ fontSize: 22 }}></i>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }
}

RemoteActionCustomerSingleAddForm = reduxForm({
  form: 'RemoteActionCustomerSingleAddForm',
})(RemoteActionCustomerSingleAddForm)

const mapStateToProps = state => ({
  auth: state.auth,
  role: state.auth.role,
  type: state.auth.type,
  fullname: state.auth.fullname,
  category: state.auth.category,
})

const mapDispatchToProps = dispatch => ({
  addRemoteAction: bindActionCreators(addRemoteAction, dispatch),
  fetchRemoteActions: bindActionCreators(fetchRemoteActions, dispatch),
  dispatch,
})

export default connect(mapStateToProps, mapDispatchToProps)(RemoteActionCustomerSingleAddForm)

